import { useEffect, useRef, useState, useCallback } from 'react';
import Layout from '../../layouts/Layout';
import { apiFetch } from '../../api';
import * as styles from '../packaging/PackagingSlip.styles';
import { useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import Select from "react-select";

export default function TestCertificatePage() {
  const navigate = useNavigate();
  const PAGE_SIZE = 10;

  const [visibleItems, setVisibleItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  /* =========================
     CERT HEADER
  ========================= */
  // Helper function to get current datetime in local timezone for datetime-local input
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [certificate, setCertificate] = useState({
    certificate_no: '',
    test_date: getCurrentDateTime(),
    customer_name: '',
    po_no: '',
    po_date: '',
    so_no: '',
    so_date: '',
    gas_train_no: '',
    supply_as: 'gas_train', // 'gas_train' or 'individual'
  });

  /* =========================
     SCANNED ITEMS
  ========================= */
  const [items, setItems] = useState([]);
  const [scanMode, setScanMode] = useState('ITEM'); // ITEM | LOT
  const [activeItemIndex, setActiveItemIndex] = useState(null);

  /* =========================
     ITEM MASTER CACHE (ONLY FOR ITEM CHECK)
  ========================= */
  const itemCodeSetRef = useRef(new Set());
  const [itemList, setItemList] = useState([]);

  useEffect(() => {
    apiFetch('/api/item-master/fetch-item-list').then((data) => {
      const activeItems = data.filter(i => i.status === 0);
      itemCodeSetRef.current = new Set(activeItems.map(i => i.item_code));
      setItemList(activeItems);
    });
  }, []);

  /* =========================
     SCANNER BUFFER
  ========================= */
  const bufferRef = useRef('');
  const timeoutRef = useRef(null);
  const processingRef = useRef(false);
  const testDateRef = useRef(null);
  const poDateRef = useRef(null);
  const soDateRef = useRef(null);

  /* =========================
     ITEM SCAN
  ========================= */
  const processItemScan = useCallback(async (code) => {
    const res = await apiFetch(`/api/item-master/find-item?code=${code}`);
    if (!res?.success) {
      alert('Item not found');
      return;
    }

    setItems((prev) => {
      const updated = [...prev];
      const idx = updated.findIndex(i => i.item_code === res.item_code);
      
      if (idx !== -1) {
        // Existing item → increase qty
        const newQty = updated[idx].qty + 1;
        updated[idx] = {
          ...updated[idx],
          qty: newQty,
          total_weight: newQty * updated[idx].weight,
        };
        setActiveItemIndex(idx);
      } else {
        // New item
        updated.push({
          item_code: res.item_code,
          item_description: res.item_description || res.description || '',
          test_type: res.test_type || '',
          uom: res.uom || '',
          weight: Number(res.weight) || 0,
          qty: 1,
          total_weight: Number(res.weight) || 0,
          lot_code: '',
        });
        setActiveItemIndex(updated.length - 1);
      }
      return updated;
    });
    setScanMode('LOT');
  }, []);

  /* =========================
     LOT SCAN
  ========================= */
  const processLotScan = useCallback((lotCode) => {
    if (activeItemIndex === null) return;
    
    setItems((prev) => {
      const updated = [...prev];
      const item = updated[activeItemIndex];
      updated[activeItemIndex] = {
        ...item,
        lot_code: item.lot_code ? `${item.lot_code}, ${lotCode}` : lotCode,
      };
      return updated;
    });
    setActiveItemIndex(null);
    setScanMode('ITEM');
  }, [activeItemIndex]);

  /* =========================
     MANUAL ITEM ADDITION
  ========================= */
  const addManualItem = () => {
    setItems((prev) => [
      ...prev,
      {
        item_code: '',
        item_description: '',
        test_type: '',
        uom: '',
        weight: 0,
        qty: 1,
        total_weight: 0,
        lot_code: '',
        is_manual: true,
      }
    ]);
  };


  const handleManualQtyChange = (index, qty) => {
    setItems((prev) => {
      const updated = [...prev];

      const newQty = parseInt(qty) || 1;

      updated[index] = {
        ...updated[index],
        qty: newQty,
        total_weight: updated[index].weight * newQty,
      };

      return updated;
    });
  };

  const handleManualLotChange = (index, value) => {
    setItems((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        lot_code: value,
      };

      return updated;
    });
  };

  useEffect(() => {
    const filtered = itemList.filter((i) =>
      i.item_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const end = page * PAGE_SIZE;

    const nextItems = filtered.slice(0, end);

    console.log("Total items:", itemList.length);
    console.log("Filtered items:", filtered.length);
    console.log("Items rendering:", nextItems);

    setVisibleItems(
      nextItems.map((i) => ({
        value: i.item_code,
        label: i.item_code,
      }))
    );
  }, [itemList, page, searchTerm]);

  const handleScrollBottom = () => {
    const filtered = itemList.filter((i) =>
      i.item_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (page * PAGE_SIZE < filtered.length) {
      setPage((prev) => prev + 1);
    }
  };


  const handleManualItemChange = (index, code) => {
    const item = itemList.find((i) => i.item_code === code);
    if (!item) return;

    setItems((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        item_code: item.item_code,
        item_description: item.name || "",
        test_type: item.test_type || "",
        uom: item.uom || "",
        weight: Number(item.net_weight_per_unit) || 0,
        total_weight:
          (Number(item.net_weight_per_unit) || 0) *
          (updated[index].qty || 1),
      };

      return updated;
    });
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  /* =========================
     BARCODE LISTENER
  ========================= */
  useEffect(() => {
    const handler = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      
      if (e.key.length === 1) bufferRef.current += e.key;
      
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(async () => {
        if (processingRef.current) return;
        processingRef.current = true;
        
        const code = bufferRef.current.trim();
        bufferRef.current = '';
        
        if (code.length < 3) {
          processingRef.current = false;
          return;
        }
        
        const isItem = itemCodeSetRef.current.has(code);
        
        try {
          if (isItem) {
            setActiveItemIndex(null);
            setScanMode('ITEM');
            await processItemScan(code);
          } else {
            if (scanMode === 'LOT' && activeItemIndex !== null) {
              processLotScan(code);
            }
          }
        } finally {
          processingRef.current = false;
        }
      }, 80);
    };
    
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      clearTimeout(timeoutRef.current);
    };
  }, [scanMode, activeItemIndex, processItemScan, processLotScan]);

  /* =========================
     HANDLE INPUT CHANGES
  ========================= */
  const handleInputChange = (field, value) => {
    setCertificate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /* =========================
     SAVE CERTIFICATE
  ========================= */
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (items.length === 0) {
      alert('Please add at least one item to the certificate');
      return;
    }

    if (!certificate.certificate_no || !certificate.customer_name) {
      alert('Please fill in required fields: Certificate No and Customer Name');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        certificate: certificate,
        items: items
      };

      const response = await apiFetch('/api/test-certificate/save', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.success) {
        alert('Test Certificate saved successfully!');
        navigate('/test-certificate-index');
      } else {
        alert('Failed to save: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save certificate: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     GENERATE CERTIFICATE NUMBER
  ========================= */
  const generateCertificateNo = async () => {
    try {
      const response = await apiFetch('/api/test-certificate/generate-certificate-no');
      if (response.success) {
        handleInputChange('certificate_no', response.data.certificate_no);
      }
    } catch (error) {
      console.error('Failed to generate certificate number:', error);
    }
  };

  // Auto-generate certificate number on component mount
  useEffect(() => {
    if (!certificate.certificate_no) {
      generateCertificateNo();
    }
  }, []);

  /* =========================
     UI
  ========================= */
  return (
    <Layout>
      <div style={styles.pageContainer}>
        {/* ================= CERTIFICATE HEADER ================= */}
        <div style={styles.card}>
          <h2 style={{ ...styles.cardTitle, textAlign: 'center', marginBottom: '30px' }}>
            TEST CERTIFICATE
          </h2>

          <div style={styles.formGrid}>
            {/* Row 1 */}
            <div style={styles.formGroup}>
              <label style={styles.labelStyle}>
                <strong>Test Certificate No.</strong>
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  value={certificate.certificate_no}
                  onChange={(e) => handleInputChange('certificate_no', e.target.value)}
                  style={{ ...styles.inputStyle, flex: 1 }}
                  placeholder="Enter certificate number"
                />
                <button
                  type="button"
                  onClick={generateCertificateNo}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Auto
                </button>
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.labelStyle}>
                <strong>Date & Time of testing</strong>
              </label>
              <div style={{ position: 'relative' }}>
                <div
                  onClick={() => testDateRef.current?.showPicker()}
                  style={{
                    ...styles.inputStyle,
                    color: certificate.test_date ? '#1e40af' : '#9ca3af',
                    fontWeight: certificate.test_date ? '600' : 'normal',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {certificate.test_date ? (() => {
                    const d = new Date(certificate.test_date.replace(' ', 'T'));
                    return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                  })() : 'Select date & time'}
                </div>
                <input
                  ref={testDateRef}
                  type="datetime-local"
                  value={certificate.test_date}
                  onChange={(e) => handleInputChange('test_date', e.target.value)}
                  style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                />
              </div>
            </div>

            {/* Row 2 - Customer Name */}
            <div style={{ ...styles.formGroup, gridColumn: '1 / span 2' }}>
              <label style={styles.labelStyle}>
                <strong>Customer Name</strong>
              </label>
              <input
                type="text"
                value={certificate.customer_name}
                onChange={(e) => handleInputChange('customer_name', e.target.value)}
                style={styles.inputStyle}
                placeholder="Enter customer name"
              />
            </div>

            {/* Row 3 */}
            <div style={styles.formGroup}>
              <label style={styles.labelStyle}>
                <strong>Customer's PO No.</strong>
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  value={certificate.po_no}
                  onChange={(e) => handleInputChange('po_no', e.target.value)}
                  style={{ ...styles.inputStyle, flex: 1 }}
                  placeholder="PO number"
                />
                <div style={{ position: 'relative', width: '140px' }}>
                  <div
                    onClick={() => poDateRef.current?.showPicker()}
                    style={{
                      ...styles.inputStyle,
                      color: certificate.po_date ? '#1e40af' : '#9ca3af',
                      fontWeight: certificate.po_date ? '600' : 'normal',
                      cursor: 'pointer',
                      width: '140px',
                    }}
                  >
                    {certificate.po_date ? new Date(certificate.po_date).toLocaleDateString('en-GB') : 'PO date'}
                  </div>
                  <input
                    ref={poDateRef}
                    type="date"
                    value={certificate.po_date}
                    onChange={(e) => handleInputChange('po_date', e.target.value)}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                  />
                </div>
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.labelStyle}>
                <strong>Our SO No.</strong>
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  value={certificate.so_no}
                  onChange={(e) => handleInputChange('so_no', e.target.value)}
                  style={{ ...styles.inputStyle, flex: 1 }}
                  placeholder="SO number"
                />
                <div style={{ position: 'relative', width: '140px' }}>
                  <div
                    onClick={() => soDateRef.current?.showPicker()}
                    style={{
                      ...styles.inputStyle,
                      color: certificate.so_date ? '#1e40af' : '#9ca3af',
                      fontWeight: certificate.so_date ? '600' : 'normal',
                      cursor: 'pointer',
                      width: '140px',
                    }}
                  >
                    {certificate.so_date ? new Date(certificate.so_date).toLocaleDateString('en-GB') : 'SO date'}
                  </div>
                  <input
                    ref={soDateRef}
                    type="date"
                    value={certificate.so_date}
                    onChange={(e) => handleInputChange('so_date', e.target.value)}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                  />
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ 
              gridColumn: '1 / span 2',
              height: '1px',
              backgroundColor: '#e5e7eb',
              margin: '20px 0'
            }} />

            {/* Components supplied as */}
            <div style={{ ...styles.formGroup, gridColumn: '1 / span 2' }}>
              <label style={{ ...styles.labelStyle, marginBottom: '10px' }}>
                <strong>Components are supplied as :</strong>
              </label>
              
              <div style={{ display: 'flex', gap: '30px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="supply_as"
                    value="gas_train"
                    checked={certificate.supply_as === 'gas_train'}
                    onChange={(e) => handleInputChange('supply_as', e.target.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>A part of Gas train</span>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="supply_as"
                    value="individual"
                    checked={certificate.supply_as === 'individual'}
                    onChange={(e) => handleInputChange('supply_as', e.target.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span>Individual components</span>
                </label>
              </div>
            </div>

            {/* Gas Train Equipment No - Only show if gas train is selected */}
            {certificate.supply_as === 'gas_train' && (
              <div style={{ ...styles.formGroup, gridColumn: '1 / span 2' }}>
                <label style={styles.labelStyle}>
                  <strong>If supplied as a part of Gas train, Gas Train Equipment No</strong>
                </label>
                <input
                  type="text"
                  value={certificate.gas_train_no}
                  onChange={(e) => handleInputChange('gas_train_no', e.target.value)}
                  style={styles.inputStyle}
                  placeholder="Enter gas train equipment number"
                />
              </div>
            )}
          </div>
        </div>

        {/* ================= ITEMS TABLE ================= */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              📦 Tested Items
              <span style={styles.itemCountBadge}>
                {items.length}
              </span>
            </h3>
            <button
              onClick={addManualItem}
             style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                width: '180px'
              }}
            >
              + Add Item Manually
            </button>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.tableStyle}>
              <thead>
                <tr>
                  <th style={styles.thStyle}>Sr. No</th>
                  <th style={styles.thStyle}>Item Code</th>
                  <th style={styles.thStyle}>Item Description</th>
                  <th style={styles.thStyle}>Qty</th>
                  <th style={styles.thStyle}>Lot Code / Serial Nos.</th>
                  <th style={styles.thStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i, idx) => (
                  <tr key={idx} style={styles.trStyle(idx === activeItemIndex)}>
                    <td style={styles.tdStyle}><b>{idx + 1}</b></td>
                    <td style={styles.tdStyle}>
                      {i.is_manual && !i.item_code ? (
                        <Select
                          options={visibleItems}
                          value={
                            i.item_code
                              ? { value: i.item_code, label: i.item_code }
                              : null
                          }
                          onChange={(selected) =>
                            handleManualItemChange(idx, selected.value)
                          }
                          onMenuScrollToBottom={handleScrollBottom}
                          onInputChange={(input) => {
                            setSearchTerm(input);
                            setPage(1);
                          }}
                          placeholder="Search Item..."
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      ) : (
                        <b>{i.item_code}</b>
                      )}
                    </td>
                    <td style={styles.tdStyle}>
                      <div>{i.item_description}</div>
                      {i.test_type && (
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          fontStyle: 'italic',
                          marginTop: '4px'
                        }}>
                          ({i.test_type})
                        </div>
                      )}
                    </td>
                    <td style={styles.tdStyle}>
                      {i.is_manual ? (
                        <input
                          type="number"
                          min="1"
                          value={i.qty}
                          onChange={(e) => handleManualQtyChange(idx, e.target.value)}
                          style={{
                            width: '60px',
                            padding: '4px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            textAlign: 'center',
                          }}
                        />
                      ) : (
                        i.qty
                      )}
                    </td>
                    <td style={styles.tdStyle}>
                      {i.is_manual ? (
                        <input
                          type="text"
                          value={i.lot_code}
                          onChange={(e) => handleManualLotChange(idx, e.target.value)}
                          placeholder="Enter lot code"
                          style={{
                            width: '100%',
                            padding: '6px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '14px',
                          }}
                        />
                      ) : (
                        i.lot_code || <i style={{ color: '#9ca3af' }}>Scan lot code</i>
                      )}
                    </td>
                    <td style={styles.tdStyle}>
                      <button
                        onClick={() => removeItem(idx)}
                        style={{
                           padding: '8px 12px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                        }}
                      >
                          <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {items.length === 0 && (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 40 }}>📦</div>
                <p>Scan items or click "Add Item Manually" to populate certificate</p>
              </div>
            )}
          </div>
        </div>

        {/* ================= SCAN STATUS ================= */}
        <div style={{
          ...styles.scanStatusContainer,
          marginTop: 20,
        }}>
          <div style={styles.scanStatus}>
            <div style={styles.statusDot}></div>
            <div style={styles.statusText}>
              {scanMode === 'ITEM'
                ? 'Scan Item Barcode'
                : 'Scan Lot Code'}
            </div>
          </div>
          {activeItemIndex !== null && (
            <div style={{
              marginLeft: '20px',
              padding: '8px 12px',
              backgroundColor: '#dbeafe',
              color: '#1e40af',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
            }}>
              Active: {items[activeItemIndex]?.item_code}
            </div>
          )}
        </div>

        {/* ================= SAVE BUTTON ================= */}
        <div style={{ 
          marginTop: '30px', 
          display: 'flex', 
          justifyContent: 'center',
          gap: '15px'
        }}>
          <button
            onClick={() => navigate('/test-certificate-index')}
            style={{
              padding: '12px 30px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: '150px',
            }}
          >
            ← Back to List
          </button>
          
          <button
            onClick={handleSave}
            disabled={saving || items.length === 0}
            style={{
              padding: '12px 30px',
              backgroundColor: saving || items.length === 0 ? '#9ca3af' : '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: saving || items.length === 0 ? 'not-allowed' : 'pointer',
              minWidth: '150px',
            }}
          >
            {saving ? 'Saving...' : 'Save Certificate'}
          </button>
          
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to clear all data?')) {
                setCertificate({
                  certificate_no: '',
                  test_date: getCurrentDateTime(),
                  customer_name: '',
                  po_no: '',
                  po_date: '',
                  so_no: '',
                  so_date: '',
                  gas_train_no: '',
                  supply_as: 'gas_train',
                });
                setItems([]);
                generateCertificateNo();
              }
            }}
            style={{
              padding: '12px 30px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: '150px',
            }}
          >
            Clear All
          </button>
        </div>
      </div>
    </Layout>
  );
}